/**
 * A Google Drive reference shown from what IMAM holds: the title, where the
 * file lives, its kind, and that Google decides who can open it.
 *
 * Nothing here asks Google or IMAM's server anything. IMAM cannot read a
 * referenced file's ACL (PRD section 2), so a per-item read could only repeat
 * the stored mapping and call it "ready", which is the one thing it never knew
 * (interaction rules W3 and R3). The address is the stored one, opened only
 * when it is still a Google Drive address over https.
 */
import { ExternalLink } from "lucide-react";

import { a3Copy } from "@/components/assignments/a3-copy";
import { GoogleDriveMark } from "@/components/ui/google-drive-mark";
import { cn } from "@/lib/utils";
import { googleDriveLinkAccessibleName } from "@/lib/google-drive-labels";

import { driveDestination, driveKindLine } from "./material-facts";

const TITLE_LINK = "-my-3 inline-flex min-h-11 items-center gap-1 py-3 text-ui font-medium text-foreground underline-offset-4 hover:underline [overflow-wrap:anywhere]";

/**
 * The title as the way to the file in Drive, or the title alone when the
 * address is unusable; either way after the Drive mark, since the file is still Drive's.
 */
function DriveReferenceLink({ title, url, mimeType, className }: { title: string; url: string | null | undefined; mimeType: string | null | undefined; className?: string }) {
  const href = driveDestination(url);
  const mark = <GoogleDriveMark mimeType={mimeType} url={url} className="me-1" />;
  if (!href) return <span className={cn("inline-flex items-center gap-1 text-ui font-medium text-foreground [overflow-wrap:anywhere]", className)}>{mark}<span className="min-w-0 break-words">{title}</span></span>;
  return (
    <a href={href} target="_blank" rel="noreferrer" aria-label={googleDriveLinkAccessibleName(title)} className={cn(TITLE_LINK, className)}>
      {mark}
      <span className="min-w-0 break-words">{title}</span>
      <ExternalLink aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.75} />
    </a>
  );
}

/**
 * Where an editor of this material changes who can open the file: the file in
 * Drive, whose Share control is Google's. Never offered to a learner, who
 * cannot change it, and never pointed at a Drive the reader may not own.
 */
export function ManageDriveAccess({ url }: { url: string | null | undefined }) {
  const href = driveDestination(url);
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noreferrer" className="inline-flex min-h-11 items-center text-meta text-muted-foreground underline underline-offset-4">
      {a3Copy.manageAccessInGoogleDrive}
      <span className="sr-only">{a3Copy.opensInANewTab}</span>
    </a>
  );
}

/** One Drive reference: title, kind, Google's access decision, and for editors where to change it. */
export function DriveReference({ title, url, mimeType, canManage = false }: {
  title: string;
  url: string | null | undefined;
  mimeType: string | null | undefined;
  /** An editor of this material. Learners never are. */
  canManage?: boolean;
}) {
  const usable = driveDestination(url) !== null;
  return (
    <div className="grid justify-items-start gap-1">
      <DriveReferenceLink title={title} url={url} mimeType={mimeType} />
      <p className="text-meta text-muted-foreground">{driveKindLine(mimeType)}</p>
      <p className="text-meta text-muted-foreground">{usable ? a3Copy.googleDecidesWhoCanOpenThisFile : a3Copy.thisReferenceIsUnavailable}</p>
      {canManage && <ManageDriveAccess url={url} />}
    </div>
  );
}
